'use client'

import React from 'react'

import { Button } from '../../components/Button'
import { CheckboxField } from '../../components/CheckboxField'
import { Collapsible } from '../../components/Collapsible'
import { Loading } from '../../components/Loading'
import { Modal, ModalProps } from '../../components/Modal'
import { Arrow } from '../../icons'
import {
  CheckboxRow,
  ChildCheckboxRow,
  ChildGroupContainer,
  Container,
  ExpandButton,
} from './styles'

type CheckboxGroupChildOption<P extends string | number> = {
  label: string
  value: P
  count?: number
}

export type CheckboxGroupOption<
  P extends string | number,
  C extends string | number,
> = {
  label: string
  value: P
  count?: number
  children?: CheckboxGroupChildOption<C>[]
}

export type CheckboxGroupOptions<
  P extends string | number,
  C extends string | number,
> = CheckboxGroupOption<P, C>[]

export interface CheckboxGroupModalProps<
  P extends string | number,
  C extends string | number,
> extends ModalProps {
  /**
   * Modal title
   */
  title: string

  /**
   * Submit button label
   */
  submitLabel?: string

  /**
   * Initial values
   */
  initialValues?: { parentValues: P[]; childValues?: C[] }

  /**
   * Loading state
   */
  isLoading?: boolean

  /**
   * Checkbox options
   */
  options: CheckboxGroupOptions<P, C>

  /**
   * onSubmit callback
   */
  onSubmit: (values: { parentValues: P[]; childValues: C[] }) => void
}

const includesAll = <T,>(values: T[], targetValues: T[]) => {
  return targetValues.every((v) => values.includes(v))
}

const includesPartial = <T,>(values: T[], targetValues: T[]) => {
  return values.some((v) => targetValues.includes(v))
}

export const CheckboxGroupModal = <
  P extends string | number,
  C extends string | number,
>({
  title,
  submitLabel = '決定',
  initialValues,
  isLoading,
  options,
  onSubmit,
  ...modalProps
}: CheckboxGroupModalProps<P, C>) => {
  const [values, setValues] = React.useState<{
    parentValues: P[]
    childValues: C[]
  }>(
    Array.isArray(initialValues) || !initialValues
      ? { parentValues: [], childValues: [] }
      : {
          parentValues: initialValues.parentValues,
          childValues: initialValues.childValues || [],
        },
  )
  const [openParentValues, setOpenParentValues] = React.useState<P[]>([])

  const getLabel = (
    option: CheckboxGroupOption<P, C> | CheckboxGroupChildOption<C>,
  ) => `${option.label}${option.count ? ` (${option.count})` : ''}`

  return (
    <Modal title={title} {...modalProps}>
      <Modal.Body>
        <Container>
          {isLoading && <Loading size="md" />}
          {!isLoading &&
            options.map((option) => {
              // Nested options
              if (option.children) {
                // Get child values
                const childValues = option.children.map((c) => c.value)

                // Is parent checked
                const isParentChecked = values.parentValues.includes(
                  option.value,
                )

                // Is children is partially checked
                const hasPartial = includesPartial(
                  values.childValues,
                  childValues,
                )

                return (
                  <React.Fragment key={option.value}>
                    <CheckboxRow>
                      <CheckboxField
                        key={option.label}
                        label={getLabel(option)}
                        value={option.value}
                        checked={isParentChecked}
                        // Set indeterminate flag to true when parent is unchecked
                        // and children is partially checked
                        indeterminate={!isParentChecked && hasPartial}
                        onChange={() => {
                          // Toggle parent check state
                          setValues((prev) =>
                            prev.parentValues.includes(option.value)
                              ? {
                                  // Uncheck parent
                                  parentValues: prev.parentValues.filter(
                                    (v) => v !== option.value,
                                  ),
                                  childValues: prev.childValues.filter(
                                    (v) => !childValues.includes(v),
                                  ),
                                }
                              : {
                                  // Check parent, and filter out child values
                                  parentValues: [
                                    ...prev.parentValues,
                                    option.value,
                                  ],
                                  childValues: prev.childValues.filter(
                                    (v) => !childValues.includes(v),
                                  ),
                                },
                          )
                        }}
                      />
                      <ExpandButton
                        open={openParentValues.includes(option.value)}
                        onClick={() => {
                          // Toggle open state
                          setOpenParentValues((prev) =>
                            prev.includes(option.value)
                              ? prev.filter((v) => v !== option.value)
                              : [...prev, option.value],
                          )
                        }}
                      >
                        <Arrow direction="down" />
                      </ExpandButton>
                    </CheckboxRow>

                    <Collapsible open={openParentValues.includes(option.value)}>
                      <ChildGroupContainer>
                        {option.children.map((childOption) => (
                          <ChildCheckboxRow key={childOption.value}>
                            <CheckboxField
                              label={getLabel(childOption)}
                              value={childOption.value}
                              size="medium"
                              checked={
                                // Check child if parent is checked, or child is checked
                                isParentChecked ||
                                values.childValues.includes(childOption.value)
                              }
                              onChange={() => {
                                setValues((prev) => {
                                  // If previous state includes parent value
                                  if (
                                    prev.parentValues.includes(option.value)
                                  ) {
                                    // Uncheck child and parent
                                    return {
                                      parentValues: prev.parentValues.filter(
                                        (v) => v !== option.value,
                                      ),
                                      childValues: childValues.filter(
                                        (v) => v !== childOption.value,
                                      ),
                                    }
                                  }

                                  // If previous state includes child value
                                  if (
                                    prev.childValues.includes(childOption.value)
                                  ) {
                                    // Simply uncheck the child
                                    return {
                                      parentValues: prev.parentValues,
                                      childValues: prev.childValues.filter(
                                        (v) => v !== childOption.value,
                                      ),
                                    }
                                  }

                                  // If next state includes all children in parent
                                  const checkedState = [
                                    ...prev.childValues,
                                    childOption.value,
                                  ]
                                  if (includesAll(checkedState, childValues)) {
                                    // Check parent, and remove all children in parent
                                    return {
                                      parentValues: [
                                        ...prev.parentValues,
                                        option.value,
                                      ],
                                      childValues: prev.childValues.filter(
                                        (v) => !childValues.includes(v),
                                      ),
                                    }
                                  }

                                  // Simply check the child
                                  return {
                                    parentValues: prev.parentValues,
                                    childValues: checkedState,
                                  }
                                })
                              }}
                            />
                          </ChildCheckboxRow>
                        ))}
                      </ChildGroupContainer>
                    </Collapsible>
                  </React.Fragment>
                )
              } else {
                // Single, un-nested options
                return (
                  <CheckboxRow key={option.value}>
                    <CheckboxField
                      label={getLabel(option)}
                      value={option.value}
                      checked={values.parentValues.includes(option.value)}
                      onChange={() => {
                        // Toggle check state
                        setValues((prev) =>
                          prev.parentValues.includes(option.value)
                            ? {
                                ...prev,
                                // Uncheck
                                parentValues: prev.parentValues.filter(
                                  (v) => v !== option.value,
                                ),
                              }
                            : {
                                ...prev,
                                // Check
                                parentValues: [
                                  ...prev.parentValues,
                                  option.value,
                                ],
                              },
                        )
                      }}
                    />
                  </CheckboxRow>
                )
              }
            })}
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary-outlined"
          size="lg"
          onClick={() => {
            onSubmit(values)
            modalProps.onClose?.()
          }}
          fullWidth
        >
          {submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
